import { ArrowLeft, Waves, LifeBuoy, Thermometer, ShowerHead, Baby, Snowflake, Lightbulb, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ShareButton } from "@/components/ShareButton";
import { GygTourBand } from "@/components/GetYourGuide";
import { CityMap } from "@/components/CityMap";

const SITE = "https://cphhighlights.com";

const SWIM_BLUE = "#0077C8";
const KIDS_GREEN = "#16A34A";

const BATHS = [
  {
    name: "Islands Brygge Havnebad",
    meta: "Islands Brygge · The original",
    addr: "Islands Brygge 7",
    coords: [55.66852, 12.57751],
    main: true,
    kids: true,
    body: "The one that started it all: five pools including a children's and a diving pool, big lawns for sunbathing and a front-row view of the harbour traffic. Packed on any warm day — come before noon for a spot on the grass.",
    link: { label: "The Islands Brygge guide", to: "/areas/islands-brygge" },
  },
  {
    name: "Fisketorvet Havnebadet",
    meta: "Kalvebod Brygge · Diving basin",
    addr: "Kalvebod Brygge 55",
    coords: [55.66302, 12.56471],
    main: true,
    kids: true,
    body: "The bath by the Fisketorvet shopping centre — three basins with a proper deep diving pool. More open and breezy than Islands Brygge, and usually a little less crowded. One basin stays shallow and roped-off, so it works for kids too.",
    link: { label: "The Vesterbro guide", to: "/areas/vesterbro" },
  },
  {
    name: "Kalvebod Bølge — The Wave",
    meta: "Kalvebod Brygge · The wavy deck",
    addr: "Kalvebod Brygge 59",
    coords: [55.6627, 12.5638],
    body: "The rolling wooden promenade just across from the Fisketorvet bath. Kayak polo and sunbathing on the curves — swimming happens at the bath next door, but the deck is the nicest hangout of the two.",
    link: { label: "The Vesterbro guide", to: "/areas/vesterbro" },
  },
  {
    name: "Halfdansgade",
    meta: "Islands Brygge · Local quay steps",
    addr: "Halfdansgade 1",
    coords: [55.66334, 12.5805],
    body: "A low-key set of harbour steps further down the Islands Brygge waterfront, past the main bath — quieter, with the same clean water and a neighbourhood crowd.",
    link: { label: "The Islands Brygge guide", to: "/areas/islands-brygge" },
  },
  {
    name: "Havnegade",
    meta: "Indre By · Inner-harbour deck",
    addr: "Havnegade 45",
    coords: [55.6779, 12.5915],
    body: "The wooden harbour-front deck on the city-centre side near Nyhavn — sun loungers, ladders into the inner harbour and a front-row view of the boats. Central and buzzy on a warm day.",
    link: { label: "The Indre By guide", to: "/areas/indre-by" },
  },
  {
    name: "Sandkaj",
    meta: "Nordhavn · The new quay",
    addr: "Sandkaj 30",
    coords: [55.70621, 12.59718],
    main: true,
    body: "Nordhavn's sleek bathing zone — a big sun deck and steps straight into deep, clear water. Fewer frills than Islands Brygge, more of a dive-off-the-quay local scene for confident swimmers.",
    link: { label: "The Nordhavn guide", to: "/areas/nordhavn" },
  },
  {
    name: "Stubkaj",
    meta: "Nordhavn · Calm & shallow",
    addr: "Stubkaj 1",
    coords: [55.70931, 12.59475],
    kids: true,
    body: "Just along the Nordhavn waterfront from Sandkaj — a smaller, calmer bathing spot with shallower water and an easy edge. The gentlest Nordhavn pick for small swimmers.",
    link: { label: "The Nordhavn guide", to: "/areas/nordhavn" },
  },
  {
    name: "Søndre Refshalebassin",
    meta: "Refshaleøen · Wild basin",
    addr: "Refshalevej 145A",
    coords: [55.68988, 12.61015],
    body: "A wilder swim in the old shipyard basin on Refshaleøen, out by Reffen and La Banchina — rough-and-ready and industrial, with none of the downtown polish.",
    link: { label: "The Refshaleøen guide", to: "/areas/refshaleoen" },
  },
  {
    name: "La Banchina",
    meta: "Refshaleøen · Wine & a swim ladder",
    addr: "Refshalevej 141A",
    coords: [55.6906, 12.6079],
    body: "A tiny wine-and-coffee shack on the Refshaleøen quay with a swim ladder straight into the harbour and a wood-fired sauna — the cult local spot for a dip and a glass, summer or winter.",
    link: { label: "The Refshaleøen guide", to: "/areas/refshaleoen" },
  },
  {
    name: "Badezone Teglholm Brygge",
    meta: "Teglholmen · Quiet quay",
    addr: "Teglholmsgade 10E",
    coords: [55.65132, 12.54617],
    body: "A quiet neighbourhood bath on Teglholmen in Sydhavn — a sun deck and ladders into calm basin water, well away from the summer crowds.",
    link: { label: "All neighbourhoods", to: "/areas" },
  },
  {
    name: "Vandtrappen",
    meta: "Teglholmen · Water staircase",
    addr: "Richard Boones Vej",
    coords: [55.64778, 12.54704],
    body: "The 'water staircase' on Teglholmen — broad wooden steps running straight down into the harbour for a gentle, gradual wade-in. A laid-back local Sydhavn spot.",
    link: { label: "All neighbourhoods", to: "/areas" },
  },
];

const MARKERS = BATHS.map((b) => ({
  name: b.name,
  coords: b.coords,
  color: b.kids ? KIDS_GREEN : SWIM_BLUE,
  big: !!b.main,
  tag: b.kids ? `Great for kids · ${b.addr}` : b.addr,
  to: b.link?.to,
}));

const MAIN = BATHS.filter((b) => b.main);
const MORE = BATHS.filter((b) => !b.main);

const SAUNAS = [
  "Sauna ved Fisketorvet",
  "Sauna ved Kalvebod Bølge",
  "Sauna ved Søndre Refshalebassin",
  "Sauna ved Teglholm Brygge",
];

const PRACTICAL = [
  {
    icon: LifeBuoy,
    title: "Season & lifeguards",
    body: "The baths are open May to September, free, with lifeguards at the main pools in high summer. Outside the flagged zones you're on your own.",
  },
  {
    icon: Thermometer,
    title: "The water",
    body: "Clean enough to earn the Blue Flag most years — the city posts daily water-quality flags at each bath. Cold until June; locals call 16°C 'fine'.",
  },
  {
    icon: ShowerHead,
    title: "Facilities",
    body: "Basic: open-air showers and portable toilets at the big baths, minimal elsewhere. Bring your own towel and a dry bag for your phone.",
  },
  {
    icon: Baby,
    title: "Kids",
    body: "Islands Brygge and Fisketorvet have shallow, roped-off children's pools — the safest picks for small swimmers.",
  },
  {
    icon: Snowflake,
    title: "Winter dipping",
    body: (
      <>
        The hardcore keep swimming all year. If you want the Danish cold-plunge ritual with a sauna
        after, book La Banchina on Refshaleøen or{" "}
        <a
          href="https://gyg.me/R0n6X0TA"
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          CopenHot&apos;s harbour saunas
        </a>
        .
      </>
    ),
    link: { label: "The Refshaleøen guide", to: "/areas/refshaleoen" },
  },
];

const SWIM_FAQ = [
  {
    q: "Is the harbour really clean enough to swim in?",
    a: "Yes — Copenhagen's harbour has been officially swimmable for over 20 years and most baths carry Blue Flag water quality. After very heavy rain the city advises waiting a day; water quality is flagged at each bath.",
  },
  {
    q: "Do the harbour baths cost anything?",
    a: "No — all the official harbour baths are free and open to everyone, May to September.",
  },
  {
    q: "Which harbour bath is best for kids?",
    a: "Islands Brygge has a shallow, lifeguarded children's pool and big lawns. In Nordhavn, Stubkaj and Sandkaj have calm, shallow corners that suit small swimmers. The Teglholmen and Refshaleøen basins are deeper and suit confident swimmers.",
  },
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: SWIM_FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Harbour Swimming in Copenhagen: The Official Baths",
  url: `${SITE}/harbour-swimming`,
  description:
    "Where to swim in Copenhagen's harbour: Islands Brygge, Fisketorvet, Kalvebod Bølge, Sandkaj, Stubkaj and more — free, clean and right in the city.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Harbour Swimming", item: `${SITE}/harbour-swimming` },
  ],
};

const SwimCard = ({ b }) => {
  const i = BATHS.indexOf(b);
  return (
    <Reveal delay={(i % 2) * 0.05} y={30}>
      <div
        data-testid={`swim-spot-${i}`}
        className="h-full bg-white border border-zinc-200 shadow-brut rounded-xl p-6 md:p-7 hover:-translate-y-0.5 hover:shadow-xl transition-all"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="font-body font-700 text-[11px] tracking-widest text-[#DF3226]">{b.meta.toUpperCase()}</p>
          <div className="flex items-center gap-1.5 shrink-0">
            {b.main && (
              <span className="inline-flex items-center font-body font-800 text-[10px] tracking-wide uppercase text-white bg-[#DF3226] rounded-full px-2.5 py-1">
                Main spot
              </span>
            )}
            {b.kids && (
              <span className="inline-flex items-center gap-1 font-body font-800 text-[10px] tracking-wide uppercase text-white bg-[#16A34A] rounded-full px-2.5 py-1">
                <Baby size={12} /> Kids
              </span>
            )}
          </div>
        </div>
        <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-2 leading-none">{b.name}</h2>
        {b.addr && <p className="font-body text-xs text-black/45 mt-1.5">{b.addr}</p>}
        <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed">{b.body}</p>
        {b.link && (
          <Link
            to={b.link.to}
            data-testid={`swim-spot-link-${i}`}
            className="inline-block mt-4 font-body font-700 text-sm text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
          >
            {b.link.label}
          </Link>
        )}
      </div>
    </Reveal>
  );
};

export default function HarbourSwimmingPage() {
  return (
    <main data-testid="harbour-swimming-page" className="bg-[#F9F8F6] text-black font-body">
      <Seo
        title="Harbour Swimming in Copenhagen: The Official Baths & Spots"
        description="Where to swim in Copenhagen's harbour: Islands Brygge, Fisketorvet, Kalvebod Bølge, Sandkaj, Stubkaj and more — free harbour baths, a map and water quality tips."
        path="/harbour-swimming"
        image="https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/0f3tr0l7_2026-08-16%2023_45_31.721%2B0200.webp"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative overflow-hidden bg-[#13141A] text-white border-b border-zinc-200 pt-16 md:pt-20">
        <div className="absolute inset-0">
          <img src={"https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/0f3tr0l7_2026-08-16%2023_45_31.721%2B0200.webp"} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-12 md:mb-14">
              <Link
                to="/"
                data-testid="harbour-swim-back"
                className="inline-flex items-center gap-2 font-body font-700 text-sm bg-[#DF3226] text-white px-5 py-2.5 rounded-full border border-black shadow-brut hover:-translate-y-0.5 transition-all"
              >
                <ArrowLeft size={16} /> Back to start
              </Link>
              <ShareButton title="Harbour Swimming in Copenhagen" testid="swim-share" />
            </div>
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4">
              SWIM THE HARBOUR
            </p>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
              Copenhagen harbour swimming<br />
              <span className="font-editorial italic font-600">Every spot, ranked by locals</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/80 max-w-2xl mt-6 leading-relaxed">
              Copenhagen's biggest flex is a harbour so clean you can swim in it — downtown. From May
              to September the whole city heads to the water. Here's every official bath, plus the
              practical bits nobody tells you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* the baths — main first */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl mb-2">The main baths</h2>
          <p className="font-body text-sm md:text-base text-black/55 mb-8 max-w-2xl">
            Start here — the three biggest, best-equipped and most central harbour baths.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {MAIN.map((b) => (
              <SwimCard key={b.name} b={b} />
            ))}
          </div>

          <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl mt-16 md:mt-20 mb-8">
            More places to swim
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {MORE.map((b) => (
              <SwimCard key={b.name} b={b} />
            ))}
          </div>
        </div>
      </section>

      {/* map */}
      <section className="pb-6 md:pb-10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
            <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl">Every spot on the map</h2>
            <div className="flex items-center gap-5 font-body text-sm font-700">
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full border-2 border-white" style={{ background: SWIM_BLUE, boxShadow: "0 1px 4px rgba(0,0,0,.3)" }} />
                Harbour bath
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full border-2 border-white" style={{ background: KIDS_GREEN, boxShadow: "0 1px 4px rgba(0,0,0,.3)" }} />
                Great for kids
              </span>
            </div>
          </div>
          <div data-testid="swim-map" className="rounded-xl overflow-hidden border border-black shadow-brut h-[380px] md:h-[460px]">
            <CityMap markers={MARKERS} fitToMarkers scrollWheelZoom={false} />
          </div>
          <p className="font-body text-xs text-black/45 mt-3">Tap a pin for the address, a link to Maps and the neighbourhood guide.</p>
        </div>
      </section>

      {/* floating saunas */}
      <section className="pb-6 md:pb-10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="bg-white border border-zinc-200 shadow-brut rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-2">
              <Flame size={20} className="text-[#DF3226]" />
              <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl">Floating saunas</h2>
            </div>
            <p className="font-body text-sm md:text-base text-black/70 mt-2 leading-relaxed max-w-2xl">
              Member-run floating saunas sit right by several of the baths — warm up, then plunge
              straight into the harbour.
            </p>
            <ul className="flex flex-wrap gap-2 mt-5">
              {SAUNAS.map((s) => (
                <li
                  key={s}
                  data-testid={`swim-sauna-${s.replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 font-body font-700 text-sm bg-[#F9F8F6] border border-zinc-200 rounded-full px-3.5 py-1.5"
                >
                  <Flame size={13} className="text-[#DF3226]" /> {s}
                </li>
              ))}
            </ul>
            <p className="font-body text-xs text-black/45 mt-5 italic">
              The floating saunas are run by associations (foreninger). On selected days there's
              public access for a fee.
            </p>
          </div>
        </div>
      </section>

      {/* planner CTA */}
      <section data-testid="swim-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-24 text-center">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">MAKE A DAY OF IT</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl mt-4 max-w-2xl mx-auto">
              Build the swim into <span className="font-editorial italic font-600">your day</span>
            </h2>
            <p className="font-body text-base text-white/80 max-w-xl mx-auto mt-5">
              The harbour baths are in the planner too — add one to your itinerary and it slots in with
              the rest of your day.
            </p>
            <Link
              to="/#planner"
              data-scroll="#planner"
              data-testid="swim-cta-planner"
              className="mt-8 inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
            >
              Open the planner
            </Link>
          </Reveal>
        </div>
      </section>

      {/* practical */}
      <section className="pb-12 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl mb-8">Before you dive in</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PRACTICAL.map((p, i) => (
              <div key={p.title} data-testid={`swim-tip-${i}`} className="bg-white border border-zinc-200 rounded-xl p-5">
                <p.icon size={20} className="text-[#DF3226]" />
                <h3 className="font-display font-900 tracking-tight text-base mt-3">{p.title}</h3>
                <p className="font-body text-xs text-black/70 mt-2 leading-relaxed">{p.body}</p>
                {p.link && (
                  <Link
                    to={p.link.to}
                    data-testid={`swim-tip-link-${i}`}
                    className="inline-block mt-3 font-body font-700 text-xs text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
                  >
                    {p.link.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
          <div className="bg-[#FCEFC5] border border-black shadow-brut rounded-xl p-5 md:p-6 flex items-start gap-3 mt-8 max-w-3xl">
            <Lightbulb size={20} className="shrink-0 mt-0.5 text-[#B8860B]" />
            <p className="font-body text-sm md:text-base text-black/75 leading-relaxed">
              Local etiquette: keep the lawns tidy, don't bomb the kids' pool, and yes — the naked
              morning dippers at the far end are a Copenhagen tradition, not an emergency.
            </p>
          </div>
        </div>
      </section>

      <GygTourBand tourId="888258" label="SEE THE HARBOUR FROM A BOAT" />

      <ExploreLinks
        testid="explore-swim"
        links={[
          { label: "Islands Brygge — the area guide", to: "/areas/islands-brygge" },
          { label: "Nordhavn & Sandkaj — the area guide", to: "/areas/nordhavn" },
          { label: "Rainy instead? The indoor guide", to: "/rainy-day" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section className="pb-12 md:pb-20 border-t border-dotted border-zinc-300 pt-12 md:pt-20">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl mb-6">Quick answers</h2>
          <div className="space-y-3">
            {SWIM_FAQ.map((f, i) => (
              <details
                key={f.q}
                data-testid={`swim-faq-${i}`}
                className="group bg-white border border-zinc-200 rounded-xl px-5 py-4"
              >
                <summary className="cursor-pointer list-none font-display font-800 text-base md:text-lg flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-[#DF3226] text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="Harbour Swimming in Copenhagen" />
    </main>
  );
}
